import React, { useEffect, useMemo, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type ValueKind, valueText } from './format';

export type Column<Row> = {
  key: string;
  label: string;
  kind: ValueKind;
  value: (row: Row) => string | number | null;
};

type Props<Row> = {
  title: string;
  caption: string;
  rows: Array<Row>;
  columns: Array<Column<Row>>;
  rowKey: (row: Row) => string;
  defaultSort: string;
  onCsv: (rows: Array<Row>) => void;
  searchable?: boolean;
  search?: string;
  onSearchChange?: (search: string) => void;
};

export default function AgentHubTable<Row>({
  title,
  caption,
  rows,
  columns,
  rowKey,
  defaultSort,
  onCsv,
  searchable,
  search = '',
  onSearchChange,
}: Props<Row>) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [sort, setSort] = useState(defaultSort);
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(0), [rows, search]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return rows;
    }
    const texts = columns.filter((column) => column.kind === 'text');
    return rows.filter((row) =>
      texts.some((column) =>
        String(column.value(row) ?? '')
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [rows, columns, search]);
  const sorted = useMemo(() => {
    const column = columns.find((item) => item.key === sort) ?? columns[0];
    const sign = direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      if (left === right) {
        return 0;
      }
      if (left === null || left === '') {
        return 1;
      }
      if (right === null || right === '') {
        return -1;
      }
      return (
        (typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right))) * sign
      );
    });
  }, [filtered, columns, sort, direction]);
  const order = (key: string) => {
    setDirection(sort === key && direction === 'desc' ? 'asc' : 'desc');
    setSort(key);
  };
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        sx={{ px: 2, py: 1.25, bgcolor: tokens.plateHover, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Typography component="h2" variant="h6" sx={{ fontSize: 17 }}>
          {title}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center">
          {searchable && (
            <TextField
              size="small"
              placeholder="Search booking, order, item…"
              value={search}
              onChange={(event) => onSearchChange?.(event.target.value)}
              inputProps={{ 'aria-label': `Search ${title}` }}
            />
          )}
          <Button size="small" onClick={() => onCsv(sorted)}>
            Download CSV
          </Button>
        </Stack>
      </Stack>
      <TableContainer sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader aria-label={title}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.kind === 'text' ? 'left' : 'right'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <TableSortLabel
                    active={sort === column.key}
                    direction={sort === column.key ? direction : 'desc'}
                    onClick={() => order(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice(page * pageSize, (page + 1) * pageSize).map((row) => (
              <TableRow key={rowKey(row)} hover>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.kind === 'text' ? 'left' : 'right'}
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: column.kind === 'text' ? 'normal' : 'nowrap',
                    }}
                  >
                    {valueText(column.value(row), column.kind)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!sorted.length && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    No rows for this selection.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 1.5 }}>
        {caption}
      </Typography>
    </Paper>
  );
}
